import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import usePageSeo from "@/hooks/usePageSeo";
import { articles } from "@/mocks/gua";

export default function GuaDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  usePageSeo({
    title: article ? `${article.title} | 蝌蚪吃瓜补档` : "内容未找到 | 蝌蚪吃瓜",
    description: article
      ? article.summary
      : "该内容暂时无法查看，返回合集总览浏览更多考古旧瓜与补档合集。",
    keywords: article
      ? `${article.title},蝌蚪吃瓜,${article.categoryName},${article.tags.join(",")}`
      : "蝌蚪吃瓜,补档,考古旧瓜",
    canonical: article ? `/gua/${article.id}` : "/heji",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.summary,
            image: article.cover,
            datePublished: article.date,
            inLanguage: "zh-CN",
            author: { "@type": "Organization", name: "蝌蚪吃瓜" },
            publisher: { "@type": "Organization", name: "蝌蚪吃瓜" },
            keywords: article.tags.join(","),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://cndonglong.cn/" },
              { "@type": "ListItem", position: 2, name: "合集总览", item: "https://cndonglong.cn/heji" },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `https://cndonglong.cn/gua/${article.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-[1280px] flex-col items-center justify-center px-4 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-emotion-unhappy-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          这个合集还没整理出来
        </h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          你访问的内容可能已经调整或尚未归档。回到合集总览，那里有更多考古旧瓜与补档等着你。
        </p>
        <Link
          to="/heji"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回合集总览
        </Link>
      </div>
    );
  }

  const related = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .concat(articles.filter((a) => a.id !== article.id && a.category !== article.category))
    .slice(0, 3);

  return (
    <div className="bg-background-50">
      <div className="mx-auto max-w-[1280px] px-4 pt-6 md:px-6 md:pt-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "合集总览", to: "/heji" },
            { label: article.categoryName, to: `/heji?cat=${article.category}` },
            { label: "正文" },
          ]}
        />
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_340px]">
          <article>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              <i className="ri-price-tag-3-line"></i>
              {article.categoryName}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                整理于 {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.readCount.toLocaleString()} 次回看
              </span>
              <span className="flex items-center gap-1 text-primary-600">
                <i className="ri-fire-line"></i>
                热度 {article.hot}
              </span>
            </div>

            <div className="mt-6 h-64 w-full overflow-hidden rounded-xl bg-background-200 md:h-96">
              <img
                src={article.cover}
                alt={`${article.title} 合集配图`}
                title={`${article.title} - 蝌蚪吃瓜`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <p className="mt-6 rounded-xl border-l-4 border-primary-400 bg-primary-50 px-5 py-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              {article.summary}
            </p>

            {article.timeline && article.timeline.length > 0 && (
              <section className="mt-10">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  <i className="ri-git-commit-line text-primary-500"></i>
                  看点时间线
                </h2>
                <ol className="mt-6 border-l-2 border-background-300 pl-6">
                  {article.timeline.map((t) => (
                    <li key={t.time} className="relative pb-6 last:pb-0">
                      <span className="absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-accent-500 bg-background-50"></span>
                      <p className="text-sm font-medium text-accent-800">{t.time}</p>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-700">{t.text}</p>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {article.body && article.body.length > 0 && (
              <section className="mt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  内容详情
                </h2>
                <div className="mt-4 flex flex-col gap-4">
                  {article.body.map((p, i) => (
                    <p key={i} className="text-sm leading-loose text-foreground-700 md:text-base">
                      {p}
                    </p>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="text-sm text-foreground-500">相关标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/heji?q=${encodeURIComponent(tag)}`}
                  className="rounded-md bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {tag}
                </Link>
              ))}
            </div>

            <div className="mt-6 rounded-xl border border-background-200 bg-background-100 p-5">
              <p className="text-xs leading-relaxed text-foreground-500">
                说明：本文为公开话题的整理与阅读分享，内容已作概括化处理，不针对任何真实个人；部分信息标注为「流传版本」，请以官方公开信息为准。
              </p>
            </div>
          </article>

          <aside className="flex flex-col gap-6">
            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">本合集看点</h2>
              <ul className="mt-4 flex flex-col gap-3">
                {[
                  `所属分类：${article.categoryName}`,
                  `整理时间：${article.date}`,
                  `关键节点：${article.timeline?.length ?? 0} 个`,
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-accent-600"></i>
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">相关推荐</h2>
              <div className="mt-4 flex flex-col gap-2.5">
                {related.map((a) => (
                  <ArticleCard key={a.id} article={a} variant="compact" />
                ))}
              </div>
              <Link
                to={`/heji?cat=${article.category}`}
                className="mt-4 flex items-center justify-center gap-1.5 rounded-md border border-background-300 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                查看更多{article.categoryName}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}